import { useIsMobileView } from "hooks/utils";
import { useTranslation } from "react-i18next";
import { Grid, Icon, Button, Loader } from "semantic-ui-react";
import { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import MainButton from "components/Buttons/MainButton";
import moment from "moment";
import { searchPatientsMyLab } from "services/patient";
import SecondaryBlueButton from "components/Buttons/SecondaryBlueButton";
import CustomPagination from "components/CustomPagination";
import { displayGender } from "utils/displayGender";
import displayName from "utils/displayName";

const SearchPatient = ({
	patients,
	generalParameters,
	searchPatientsMyLab,
	addNewPatientCallback,
	isSelectSearch,
	selectCallback,
	placeholder,
	tutorOnly = false,
}) => {
	const isMobile = useIsMobileView();
	const { t } = useTranslation();
	const [currentFilterSearch, setCurrentFilterSearch] = useState("");
	const [searchStarted, setSearchStarted] = useState(false);
	const [activePage, setActivePage] = useState(1);
	const [patientsPerPage, setPatientsPerPage] = useState(10);

	const searchPatients = (page) => {
		if (!currentFilterSearch.length || currentFilterSearch.length < 3) return;
		setSearchStarted(true);
		searchPatientsMyLab({ multiple_search: currentFilterSearch }, patientsPerPage, (page - 1) * patientsPerPage + 1);
	};

	useEffect(() => {
		const timeoutId = setTimeout(() => searchPatients(activePage), 700);
		return () => clearTimeout(timeoutId);
	}, [currentFilterSearch]);

	const initSearchFilters = () => {
		setSearchStarted(false);
		setCurrentFilterSearch("");
		setActivePage(1);
	};

	const styles = {
		button: {
			backgroundColor: "#00c7b1",
			height: 40,
			width: 40,
			color: "#fff",
			fontFamily: "Lato",
			textTransform: "uppercase",
			padding: 0,
			fontSize: "0.8em",
			lineHeight: "40px",
			fontWeight: 600,
			cursor: "pointer",
			outline: "none",
			border: 0,
			borderRadius: "4px",
			boxShadow: "0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset",
			transition: "opacity .1s ease,background-color .1s ease,color .1s ease,box-shadow .1s ease,background .1s ease",
			":hover": {
				backgroundColor: "#00bda8",
				boxShadow: "0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset",
				color: "#f5f5f5",
			},
			":active": {
				backgroundColor: "#00ae99",
				color: "#e6e6e6",
			},
			":disabled": {
				backgroundColor: "#D3D3D3",
			},
		},
		icon: {
			margin: 0,
			color: "#fff",
		},
		searchIcon: {
			position: "absolute",
			right: 10,
			top: 10,
			color: "#43b39e",
		},
		rowWithoutPadding: {
			padding: !isMobile ? 0 : "0 0 0 1em",
		},
	};

	const _appointmentContainer = (status, shouldOpen, urgent) => ({
		backgroundColor:
			status === "DONE"
				? "rgba(66, 179, 158, 0.4)"
				: status === "CANCELED"
				? "rgba(145,166,180, 0.4)"
				: urgent
				? "rgba(255, 80, 77,0.4)"
				: shouldOpen
				? "rgba(253, 253, 150,0.4)"
				: "#fff",
		width: "100%",
		position: "relative",
		padding: 20,
		display: "flex",
		borderRadius: "0",
		marginTop: "5px",
		boxShadow: "0px 9px 35px -23px rgba(148,148,148,1)",
		alignItems: "center",
	});

	const firstInputRef = useRef(null);
	useEffect(() => {
		if (!searchStarted) {
			// Petite temporisation pour s'assurer que le champ est bien monté
			setTimeout(() => {
				firstInputRef.current?.focus();
			}, 0);
		}
	}, [searchStarted]);

	return (
		<>
			<Grid style={{ margin: 0, width: "100%" }}>
				<Grid.Column style={{ marginBottom: 10, padding: 0 }}>
					<Grid.Row style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: "10px" }}>
						<Grid.Column style={{ width: "100%" }}>
							<input
								style={{
									width: "100%",
									height: "40px",
									border: "solid #80808057 1px",
									borderRadius: "4px",
									padding: "8px",
								}}
								placeholder={placeholder}
								type="text"
								value={currentFilterSearch}
								onChange={(e) => {
									setActivePage(1);
									setCurrentFilterSearch(e.target.value);
								}}
								input={{ ref: firstInputRef }}
							/>
							<Button
								style={{
									backgroundColor: "transparent",
									position: "absolute",
									right: 0,
									top: 2,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									alignContent: "center",
									margin: 0,
								}}
								onClick={(e) => {
									e.preventDefault();
									initSearchFilters();
								}}
							>
								<Icon
									name={currentFilterSearch !== "" ? "eraser" : "search"}
									style={{ color: currentFilterSearch !== "" ? "red" : "#2185d0", alignSelf: "center", margin: 0 }}
								/>
							</Button>
						</Grid.Column>
					</Grid.Row>
				</Grid.Column>
			</Grid>
			{patients.search.larger && !patients.search.loading && searchStarted && (
				<Grid style={{ margin: 0, width: "100%" }}>
					<Grid.Row style={{ width: "100%", backgroundColor: "red" }}>
						<Grid.Column style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
							<div style={{ display: "flex", color: "#fff", fontWeight: 600, width: "100%" }}>
								<Icon name="user x" size="large" style={{ ...styles.icon, marginRight: "10px" }} />{" "}
								<span>
									{t("foldersOverview.patientNotFound")} <span style={{ textTransform: "uppercase" }}>{t("foldersOverview.withThisNumber")}</span>{" "}
								</span>
							</div>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			)}
			{currentFilterSearch.length >= 2 && searchStarted && (
				<Grid style={{ margin: 0 }}>
					<Grid.Row style={{ ...styles.roundHeaderContainer, backgroundColor: patients.search.larger ? "darkorange" : "#43b39e" }}>
						<Grid.Column
							style={{
								...styles.columnFlex,
								justifyContent: "space-between",
								width: "100%",
							}}
							mobile={16}
							tablet={16}
							computer={16}
						>
							<div
								style={{
									...styles.columnFlex,
									justifyContent: "flex-start",
									color: "#fff",
									fontWeight: 600,
								}}
							>
								{patients.search.loading && (
									<div style={{ display: "flex" }}>
										<Loader active size="mini" />
									</div>
								)}
								{patients.search.pagination && Math.floor(patients.search.pagination.total / patientsPerPage) > 1 && (
									<Icon name="warning sign" color="red" style={styles.icon} />
								)}
								{patients.search.pagination && Math.floor(patients.search.pagination.total / patientsPerPage) < 2 && (
									<Icon name={patients.search.pagination.total === 0 ? "user x" : "users"} style={{ ...styles.icon, marginRight: "15px" }} />
								)}
								{!patients.search.loading && patients.search.list && patients.search.pagination && (
									<span>
										{patients.search.pagination.total === 0 ? "Aucun" : patients.search.pagination.total} {t("foldersOverview.patient")}
										{patients.search.pagination.total > 1 ? "s" : ""} {t("foldersOverview.find")}
										{patients.search.pagination.total > 1 ? "s" : ""}
										{patients.search.larger ? (
											<span style={{ textTransform: "uppercase" }}>{t("foldersOverview.sameBirthIncompletRegisterNumber")}</span>
										) : (
											"."
										)}
									</span>
								)}
							</div>

							{patients.search.pagination && Math.ceil(patients.search.pagination.total / patientsPerPage) > 1 && (
								<div
									style={{
										...styles.columnFlex,
										color: "#fff",
										fontWeight: 600,
										fontSize: "1.1em",
									}}
								>
									{t("foldersOverview.refineSearch")}
								</div>
							)}

							{patients.search.pagination && Math.ceil(patients.search.pagination.total / patientsPerPage) > 1 && (
								<div
									style={{
										...styles.columnFlex,
										color: "#fff",
										fontWeight: 600,
									}}
								>
									<span>
										{t("foldersOverview.page")} {activePage}/{Math.ceil(patients.search.pagination.total / patientsPerPage)}{" "}
									</span>
								</div>
							)}
						</Grid.Column>
					</Grid.Row>

					{patients.search.loading && (
						<div style={{ width: "100%", textAlign: "center", marginTop: "1em", marginBottom: "1em" }}>
							<Loader active inline />
						</div>
					)}

					{!patients.search.loading &&
						patients.search.list?.map((l, index) => {
							return (
								<Grid.Row key={"appoint" + index} style={_appointmentContainer(index % 2 === 0)}>
									<Grid.Column mobile={16} tablet={8} computer={8}>
										<div>
											<b>
												{displayGender(l, generalParameters.data)} {displayName(l)}
											</b>
											<br />
											{l.birth_date && (
												<>
													<b style={{ fontSize: "0.8em" }}>
														{" "}
														{t(l.gender === "f" ? "foldersOverview.bornF" : "foldersOverview.bornM")}
														{t("foldersOverview.the")}
														{moment(l.birth_date).format(process.env.REACT_APP_DATE_DISPLAY_FORMAT)}
													</b>
													<span>{" - "}</span>
													<span style={{ fontSize: "0.8em", fontStyle: "italic" }}>
														{moment().diff(l.birth_date, "years")} {t("foldersOverview.years")}
													</span>
												</>
											)}
										</div>
									</Grid.Column>

									<Grid.Column mobile={16} tablet={4} computer={4} style={{}}>
										<div>
											<b style={{ fontSize: "0.8em" }}>
												{t("foldersOverview.number")}
												{l.cns}
											</b>
										</div>
										{l.active && (
											<div style={{ fontSize: "0.8em" }}>
												<Icon name="heart" /> {t("foldersOverview.actifMylab")}
											</div>
										)}

										{l.addresses && l.addresses.home && l.addresses.home.city && (
											<div style={{ fontSize: "0.8em", fontStyle: "italic" }}>
												<Icon name="home" />
												{l.addresses.home.number} {l.addresses.home.street} <br />
												{l.addresses.home.postal_code} {l.addresses.home.city}
											</div>
										)}
									</Grid.Column>

									{isSelectSearch && (
										<Grid.Column mobile={16} tablet={4} computer={4} style={{ textAlign: "right" }}>
											<MainButton
												text={"Choisir"}
												onClick={() => {
													selectCallback(l);
													initSearchFilters();
												}}
											/>
										</Grid.Column>
									)}
								</Grid.Row>
							);
						})}
					{!patients.search.loading &&
						patients.search.list &&
						patients.search.pagination &&
						(patients.search.pagination.total === 0 ||
							activePage === Math.ceil(patients.search.pagination.total / patients.search.pagination.limit)) && (
							<div
								style={{
									width: "100%",
									textAlign: "center",
									marginTop: "1em",
									marginBottom: "1em",
								}}
							>
								<SecondaryBlueButton
									text={tutorOnly ? "Créer un proche" : "Créer un nouveau patient"}
									onClick={(e) => {
										if (e !== undefined) e.preventDefault();
										initSearchFilters();
										addNewPatientCallback();
									}}
								/>
							</div>
						)}
					{!patients.search.loading && patients.search.pagination && Math.ceil(patients.search.pagination.total / patientsPerPage) > 1 && (
						<Grid.Row>
							<CustomPagination
								activePage={activePage}
								totalPages={Math.ceil(patients.search.pagination.total / patientsPerPage)}
								onPageChange={(e, { activePage }) => {
									setActivePage(activePage);
									searchPatients(activePage);
								}}
							/>
						</Grid.Row>
					)}
				</Grid>
			)}
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		user: state.user,
		patients: state.patients,
		generalParameters: state.generalParameters,
	};
};

const mapDispatchToProps = {
	searchPatientsMyLab,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchPatient);
